import { supabase } from './supabaseClient';
import type { RdvFormData, RdvWithDetails, CallerStats } from '../../types/admin';

export const callerService = {
  // Récupérer toutes les campagnes pour la sélection (pas seulement les actives)
  async getAllCampaigns() {
    const { data, error } = await supabase
      .from('campaigns')
      .select(`
        id,
        nom,
        user_id,
        statut,
        lance
      `)
      .order('nom');

    if (error) {
      console.error('Erreur lors de la récupération des campagnes:', error);
      throw error;
    }

    return data || [];
  },

  // Récupérer les campagnes actives pour la création de RDV
  async getActiveCampaigns() {
    const { data, error } = await supabase
      .from('campaigns')
      .select(`
        id,
        nom,
        user_id,
        statut,
        lance
      `)
      .eq('lance', true)
      .in('statut', ['Lancée', 'En cours'])
      .order('nom');

    if (error) {
      console.error('Erreur lors de la récupération des campagnes:', error);
      throw error;
    }

    return data || [];
  },

  // Créer un nouveau RDV
  async createRdv(rdvData: RdvFormData, callerId: string, leadId?: string): Promise<any> {
    try {
      if (leadId) {
        // Mode mise à jour d'un lead existant
        const updateData = {
          rdv: true,
          rdv_date: rdvData.rdv_date,
          rdv_commentaire: rdvData.rdv_commentaire || null,
          rdv_source: rdvData.rdv_source,
          etat_rdv: rdvData.etat_rdv,
          prospection_etape: rdvData.prospection_etape,
          campaign_id: rdvData.campaign_id,
          caller_id: callerId,
          updated_at: new Date().toISOString(),
        };

        const { data, error } = await supabase
          .from('leads')
          .update(updateData)
          .eq('id', leadId)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Mode création d'un nouveau lead
        const leadData = {
          prenom: rdvData.prenom,
          nom: rdvData.nom,
          entreprise: rdvData.entreprise,
          email: rdvData.email || null,
          telephone: rdvData.telephone || null,
          campaign_id: rdvData.campaign_id,
          rdv: true,
          rdv_date: rdvData.rdv_date,
          rdv_commentaire: rdvData.rdv_commentaire || null,
          rdv_source: rdvData.rdv_source,
          etat_rdv: rdvData.etat_rdv,
          prospection_etape: rdvData.prospection_etape,
          caller_id: callerId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        const { data, error } = await supabase
          .from('leads')
          .insert(leadData)
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      console.error('Erreur lors de la création du RDV:', error);
      throw error;
    }
  },

  // Récupérer les RDV du caller connecté
  async getCallerRdv(callerId?: string): Promise<RdvWithDetails[]> {
    let query = supabase
      .from('leads')
      .select(`
        id,
        prenom,
        nom,
        entreprise,
        email,
        telephone,
        rdv,
        rdv_date,
        rdv_commentaire,
        rdv_source,
        etat_rdv,
        prospection_etape,
        retour_client,
        created_at,
        updated_at,
        campaign_id,
        campaigns!inner(
          id,
          nom,
          user_id
        )
      `)
      .eq('rdv', true)
      .order('rdv_date', { ascending: false });

    // Filtrer par caller_id si fourni
    if (callerId) {
      query = query.eq('caller_id', callerId);
    }

    const { data: leads, error } = await query;

    if (error) {
      console.error('Erreur lors de la récupération des RDV:', error);
      throw error;
    }

    // Récupérer les profils des clients séparément
    const userIds = Array.from(new Set(leads?.map((lead: any) => lead.campaigns.user_id) || []));
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, full_name, client_name, email')
      .in('id', userIds);

    if (profilesError) {
      console.error('Erreur lors de la récupération des profils:', profilesError);
      throw profilesError;
    }

    const profilesMap = new Map(profiles?.map((p: any) => [p.id, p]) || []);

    return leads?.map((lead: any) => {
      const profile = profilesMap.get(lead.campaigns.user_id);
      return {
        id: lead.id,
        prenom: lead.prenom,
        nom: lead.nom,
        entreprise: lead.entreprise,
        email: lead.email,
        telephone: lead.telephone,
        rdv_date: lead.rdv_date!,
        rdv_commentaire: lead.rdv_commentaire,
        rdv_source: lead.rdv_source as 'Mail' | 'LinkedIn' | 'Phone',
        etat_rdv: lead.etat_rdv!,
        prospection_etape: lead.prospection_etape!,
        retour_client: lead.retour_client,
        campaign_id: lead.campaign_id,
        campaign_name: lead.campaigns.nom,
        client_id: lead.campaigns.user_id,
        client_name: profile?.client_name || profile?.full_name || 'N/A',
        client_email: profile?.email || 'N/A',
        created_at: lead.created_at!,
        updated_at: lead.updated_at,
      };
    }) || [];
  },

  // Mettre à jour un RDV existant
  async updateRdv(rdvId: string, updates: Partial<RdvFormData>): Promise<any> {
    console.log('updateRdv appelé avec:', { rdvId, updates });
    
    const updateData: any = {};

    // Mapper les champs du formulaire vers les champs de la base
    if (updates.prenom !== undefined) updateData.prenom = updates.prenom;
    if (updates.nom !== undefined) updateData.nom = updates.nom;
    if (updates.entreprise !== undefined) updateData.entreprise = updates.entreprise;
    if (updates.email !== undefined) updateData.email = updates.email || null;
    if (updates.telephone !== undefined) updateData.telephone = updates.telephone || null;
    if (updates.rdv_date !== undefined) updateData.rdv_date = updates.rdv_date;
    if (updates.rdv_commentaire !== undefined) updateData.rdv_commentaire = updates.rdv_commentaire || null;
    if (updates.rdv_source !== undefined) updateData.rdv_source = updates.rdv_source;
    if (updates.etat_rdv !== undefined) updateData.etat_rdv = updates.etat_rdv;
    if (updates.prospection_etape !== undefined) updateData.prospection_etape = updates.prospection_etape;
    if (updates.campaign_id !== undefined) updateData.campaign_id = updates.campaign_id;

    updateData.updated_at = new Date().toISOString();

    console.log('Données à mettre à jour:', updateData);

    const { data, error } = await supabase
      .from('leads')
      .update(updateData)
      .eq('id', rdvId)
      .select()
      .single();

    if (error) {
      console.error('Erreur lors de la mise à jour du RDV:', error);
      throw error;
    }

    console.log('RDV mis à jour avec succès:', data);
    return data;
  },

  // Supprimer un RDV
  async deleteRdv(rdvId: string): Promise<void> {
    const { error } = await supabase
      .from('leads')
      .delete()
      .eq('id', rdvId);

    if (error) {
      console.error('Erreur lors de la suppression du RDV:', error);
      throw error;
    }
  },

  // Récupérer les statistiques personnelles du caller
  async getMyCallerStats(callerId: string): Promise<CallerStats> {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('id, full_name, client_name, email, updated_at')
      .eq('id', callerId)
      .single();

    if (error) throw error;

    // Récupérer tous les RDV créés par ce caller
    const { data: myRdv, error: rdvError } = await supabase
      .from('leads')
      .select('id, etat_rdv, created_at, rdv_date')
      .eq('rdv', true)
      .eq('caller_id', callerId);

    if (rdvError) throw rdvError;

    const totalRdvGenerated = myRdv?.length || 0;
    const rdvValidated = myRdv?.filter((r: any) => r.etat_rdv === 'Validé').length || 0;
    const rdvPending = myRdv?.filter((r: any) => r.etat_rdv === 'En attente').length || 0;
    const rdvNoShow = myRdv?.filter((r: any) => r.etat_rdv === 'No show').length || 0;
    const rdvLitige = myRdv?.filter((r: any) => r.etat_rdv === 'Litige').length || 0;
    const validationRate = totalRdvGenerated > 0 ? (rdvValidated / totalRdvGenerated) * 100 : 0;

    // Calculer la moyenne de RDV par jour (30 derniers jours)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentRdv = myRdv?.filter((r: any) => new Date(r.created_at!) > thirtyDaysAgo) || [];
    const avgRdvPerDay = recentRdv.length / 30;

    // Trouver la date du dernier RDV
    const rdvDates = myRdv
      ?.map((r: any) => r.rdv_date)
      .filter((date: any) => date != null)
      .sort((a: string, b: string) => new Date(b).getTime() - new Date(a).getTime());
    
    const lastRdvDate = rdvDates && rdvDates.length > 0 ? rdvDates[0] : null;

    return {
      caller_id: profile.id,
      caller_name: profile.full_name || 'N/A',
      caller_email: profile.email,
      totalRdvGenerated,
      rdvValidated,
      rdvPending,
      rdvNoShow,
      rdvLitige,
      validationRate: parseFloat(validationRate.toFixed(2)),
      avgRdvPerDay: parseFloat(avgRdvPerDay.toFixed(2)),
      lastActivity: profile.updated_at || '',
      lastRdvDate,
    };
  },

  // Rechercher des leads existants pour l'autocomplétion
  async searchLeads(searchTerm: string): Promise<any[]> {
    if (!searchTerm || searchTerm.length < 2) {
      return [];
    }

    try {
      // Recherche directe dans les leads
      const { data, error } = await supabase
        .from('leads')
        .select(`
          id,
          prenom,
          nom,
          entreprise,
          email,
          telephone,
          campaign_id
        `)
        .or(`prenom.ilike.%${searchTerm}%,nom.ilike.%${searchTerm}%,entreprise.ilike.%${searchTerm}%`)
        .limit(10)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erreur lors de la recherche de leads:', error);
        return [];
      }

      // Si on a des résultats, récupérer les informations des campagnes séparément
      if (data && data.length > 0) {
        const campaignIds = Array.from(new Set(data.map((lead: any) => lead.campaign_id).filter(Boolean)));
        
        if (campaignIds.length > 0) {
          const { data: campaigns, error: campaignsError } = await supabase
            .from('campaigns')
            .select('id, nom, user_id')
            .in('id', campaignIds);

          if (!campaignsError && campaigns) {
            const campaignsMap = new Map(campaigns.map((c: any) => [c.id, c]));
            
            return data.map((lead: any) => ({
              ...lead,
              campaigns: lead.campaign_id ? campaignsMap.get(lead.campaign_id) : null
            }));
          }
        }
      }

      return data || [];
    } catch (err) {
      console.error('Erreur générale lors de la recherche de leads:', err);
      return [];
    }
  },
};

// Exports pour la compatibilité avec les imports existants
export const getAllCampaigns = callerService.getAllCampaigns;
export const getActiveCampaigns = callerService.getActiveCampaigns;
export const createRdv = callerService.createRdv;
export const getCallerRdv = callerService.getCallerRdv;
export const updateRdv = callerService.updateRdv;
export const deleteRdv = callerService.deleteRdv;
export const getMyCallerStats = callerService.getMyCallerStats;
export const searchLeads = callerService.searchLeads; 